import { PromoDetail } from "@/core/entities/promotion/PromoDetail";
import { getBlankPromotionDeductions } from "@/core/entities/promotion/PromotionDeduction";
import { useWorkspace } from "@/hooks";
import { EmptyState, Flex, Heading, Icons, Paragraph, Span, Table } from "@ir/ui";
import { memo, useEffect, useMemo, useRef } from "react";
import { usePromotionAppliedDeductions, usePromotionDrawer } from "../hooks";
import { createAppliedDeductionsTableColumns } from "../utils/appliedDeductionsTableColumns";
import { formatPromotionCurrency } from "../utils/promotionDisplay.utils";
import { PromotionsPagination } from "./PromotionsPagination";
import { SkeletonCell } from "@/presentation/ui/components/common";

interface AppliedDeductionsTabProps {
  promoDetail: PromoDetail | null;
}

const AppliedDeductionsTab = memo(({ promoDetail }: AppliedDeductionsTabProps) => {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const { settings } = useWorkspace();
  const dateFormat = settings?.displayDateFormat ?? "MMM D, YYYY";
  const numberFormat = settings?.displayNumberFormat;

  const {
    items,
    isLoading,
    isRefreshing,
    count,
    totalDeductionsClaimed,
    totalDeductionsAmount,
    currentPage,
    pageLimit,
    getPromoDeductionApplySummary,
    setCurrentPage,
    resetAppliedDeductions,
  } = usePromotionAppliedDeductions();

  const { isPromoDetailLoading } = usePromotionDrawer();

  const isRefreshingOrLoading = useMemo(
    () => isRefreshing || isLoading || isPromoDetailLoading,
    [isRefreshing, isLoading, isPromoDetailLoading]
  );

  useEffect(() => {
    if (promoDetail?.promoEventId) {
      getPromoDeductionApplySummary({
        promoEventId: promoDetail.promoEventId,
        isLoading: false,
        isRefreshing: true,
      });
    }
  }, [promoDetail?.promoEventId, promoDetail?.status, getPromoDeductionApplySummary]);

  useEffect(() => {
    if (!promoDetail?.promoEventId) {
      resetAppliedDeductions();
    }
  }, [promoDetail?.promoEventId, resetAppliedDeductions]);

  const columns = useMemo(
    () =>
      createAppliedDeductionsTableColumns({
        isRefreshing: isRefreshingOrLoading,
        dateFormat,
        numberFormat,
      }),
    [isRefreshingOrLoading, dateFormat, numberFormat]
  );

  const blankRows = useMemo(() => getBlankPromotionDeductions(10), []);

  const dataSource = useMemo(() => {
    if (isRefreshingOrLoading || (items.length === 0 && !isLoading)) {
      return blankRows;
    }
    return items;
  }, [isRefreshingOrLoading, blankRows, items, isLoading]);

  const showEmptyState = !isLoading && !isRefreshing && !isPromoDetailLoading && count === 0;

  const handlePageChange = (page: number) => {
    if (!promoDetail?.promoEventId) {
      return;
    }
    setCurrentPage(page);
    getPromoDeductionApplySummary({
      promoEventId: promoDetail.promoEventId,
      isLoading: false,
      isRefreshing: true,
    });
  };

  if (showEmptyState) {
    return (
      <EmptyState
        className="bg-white px-4 py-5"
        title={
          <Heading level={6} className="text-grey-700 font-bold mt-3">
            No matched deductions yet
          </Heading>
        }
        description={
          <Paragraph className="text-grey-500 font-regular mb-0">
            No deductions have been matched to this promotion yet. <br/> Matches will appear automatically once the promotion is
            Active and deductions are ingested.
          </Paragraph>
        }
        icon={Icons.InfoOctagon}
      />
    );
  }

  return (
    <>
      <Flex
        align="center"
        gap={2}
        className="applied-deductions-summary-bar w-100 pb-3 border-bottom-1 border-color-grey-50 mb-3"
      >
        <Flex align="center" gap={0.5}>
          <Span className="text-grey-500 text-sm">Total Applied:</Span>
          <Span className="text-grey-700 text-sm font-bold">{isRefreshingOrLoading ? <SkeletonCell height={8} width={60} /> : totalDeductionsClaimed}</Span>
        </Flex>
        <Flex align="center" gap={0.5}>
          <Span className="text-grey-500 text-sm">Total Claimed:</Span>
          <Span className="text-grey-700 text-sm font-bold">
            {isRefreshingOrLoading ? <SkeletonCell height={8} width={60} /> : formatPromotionCurrency(totalDeductionsAmount, numberFormat)}

          </Span>
        </Flex>
      </Flex>
      <Flex direction="column" className="flex-1" style={{ height: "calc(100% - 120px)" }}>
        <Table
          ref={tableContainerRef}
          sticky
          border="row"
          aria-label="Applied Deductions Table"
          scroll={{ x: "100%", y: "100%" }}
          columns={columns}
          dataSource={dataSource}
        />
      </Flex>
      <PromotionsPagination
        hasPagination={count > pageLimit}
        totalCount={count}
        pageLimit={pageLimit}
        currentPage={currentPage}
        isLoading={isLoading || isRefreshing}
        onPageChange={handlePageChange}
      />
    </>
  );
});

AppliedDeductionsTab.displayName = "AppliedDeductionsTab";

export default AppliedDeductionsTab;
